import { type FC, type ReactNode, useEffect } from "react";
import { getIsInternalAnchorLinkHref } from "../helpers/urlHelpers";
import { sendAnalyticsEvent } from "../app/contexts/Analytics/events";
import { useAppContext } from "../helpers/context-hooks/useAppContext";
import type { ParentComponentType } from "../app/contexts/Analytics/eventTypes";

/**
 * Data attribute to be added to the `<a>` tag to override the default
 * analytic event
 */
export const CUSTOM_INTERNAL_LINK_CLICKED_EVENT =
  "data-custom-internal-link-clicked-event";

/**
 * Specifies the `parent_component_heading` value in the internal link clicked analytic event
 * @example
 *
 *  <a data-parent-component-heading="my heading" href="https://foo">click me</a>
 */
export const PARENT_COMPONENT_HEADING_ATTRIBUTE =
  "data-parent-component-heading";

/**
 * Specifies the `parent_component_type` value in the internal link clicked analytic event
 * @example
 *
 *  <a data-parent-component-type="alert" href="https://foo">click me</a>
 */
export const PARENT_COMPONENT_TYPE_ATTRIBUTE = "data-parent-component-type";

/**
 * Specifies the event should be sent with the event name `external_link_clicked`
 * @example
 *
 *  <a data-external-link href="https://foo">click me</a>
 */
export const EXTERNAL_LINK_ATTRIBUTE = "data-external-link";

/**
 * AnalyticsListeners listens for click events and finds the closest
 * `<a>` tag to send an internal_link_clicked (default) or external_link_clicked analytic event.
 * The default event would include these properties:
 *  - link_text
 *  - link_url
 *
 * If more properties are needed, add the `data-custom-internal-link-clicked-event` data attribute to the `<a>` tag to override the default analytic event.
 *
 * Example:
 *
 * ```
 * <a data-custom-internal-link-clicked-event="true" href="#/m/plans">Change my search criteria.</a>
 * ```
 */
export const AnalyticsListeners: FC<{
  children?: ReactNode;
}> = ({ children }) => {
  const { dispatch } = useAppContext();
  const notApplicable: ParentComponentType = "N/A";

  useEffect(() => {
    function sendInternalLinkClickEvent(event: Event) {
      const target = event.target as HTMLElement;
      const link = target.closest("a");

      if (link) {
        const isInternalAnchorLink = getIsInternalAnchorLinkHref(link.href);
        const useCustomHandler = link.hasAttribute(
          CUSTOM_INTERNAL_LINK_CLICKED_EVENT
        );
        const sendAsExternalLinkClickedEvent = link.hasAttribute(
          EXTERNAL_LINK_ATTRIBUTE
        );
        if (
          useCustomHandler ||
          isInternalAnchorLink ||
          // Design system Tooltip components incorrectly use an anchor tag instead of button
          link.classList.contains("ds-c-tooltip__trigger-link")
        ) {
          // do not send default internal link clicked event
          return;
        }
        sendAnalyticsEvent({
          event_name: `${sendAsExternalLinkClickedEvent ? "external" : "internal"}_link_clicked`,
          text: `${link.textContent}`,
          link_type: `link_${sendAsExternalLinkClickedEvent ? "external" : "other"}`,
          link_url: link.getAttribute("href") || "N/A",
          parent_component_heading:
            link.getAttribute(PARENT_COMPONENT_HEADING_ATTRIBUTE) || "N/A",
          parent_component_type: (link.getAttribute(
            PARENT_COMPONENT_TYPE_ATTRIBUTE
          ) || notApplicable) as ParentComponentType,
        });
      }
    }

    window.addEventListener("click", sendInternalLinkClickEvent);

    return () =>
      window.removeEventListener("click", sendInternalLinkClickEvent);
  }, [dispatch]);

  return <>{children}</>;
};
